import type { InMemoryProvider } from "@openfeature/server-sdk";

import { enableAppEnvsRule, enableEmailDomainsRule } from "./rules";
import type { EvaluationContext, Rule } from "./types";

/**
 * OpenFeature InMemoryProvider の FlagConfigurationを取り出して、
 * フラグ定義の型として使う
 */
type FlagConfiguration = ConstructorParameters<typeof InMemoryProvider>[0];
type Flag = NonNullable<FlagConfiguration>[string];

/**
 * フィーチャーフラグ定義
 *
 * 新しいフラグはここに追加する
 */
export const flagConfig = {
  /**
   * 新しい予約フロー（/booking）を開ける人。
   * 予約ボタンの行き先を変える条件には使わない。社員も Staff プランなどで今の予約フォームから予約するため。
   */
  "reservation-v2": buildBooleanFlag({
    defaultValue: false,
    decide: [
      enableAppEnvsRule(["dev", "feat"]),
      enableEmailDomainsRule(["sa-nu.com"]),
    ],
  }),
} as const satisfies Record<string, Flag>;

/**
 * Rule の配列を評価
 * いずれかのルールが true なら true、すべて false なら false を返す（OR条件）
 */
function evaluateRules(
  rules: readonly Rule[],
  context: EvaluationContext
): boolean {
  const isEnabled = rules.some((rule) => rule(context));
  return isEnabled;
}

/**
 * boolean フラグを作成するヘルパー関数
 *
 * @param config.defaultValue - デフォルト値
 * @param config.decide - フラグ評価ルールの配列（OR条件）
 */
function buildBooleanFlag(config: {
  defaultValue: boolean;
  decide: readonly Rule[];
}): Flag {
  return {
    disabled: false,
    variants: { on: true, off: false },
    defaultVariant: config.defaultValue ? "on" : "off",
    contextEvaluator: (context: EvaluationContext) => {
      const result =
        evaluateRules(config.decide, context) || config.defaultValue;
      return result ? "on" : "off";
    },
  };
}

export type FlagKey = keyof typeof flagConfig;
